/* eslint-disable react/display-name */
import PropTypes from 'prop-types'

import { useGremlinQuery } from 'hooks/use-gremlin-query'
import logoOpsGenie from 'assets/logos/opsgenie.svg'
import SvgMessaging from 'components/icons/Messaging'
import SvgDatabase from 'components/icons/Database'
import SvgMonitoring from 'components/icons/Monitoring'
import logoLinux from 'assets/logos/linux.svg'
import AttackTypeIcon from 'components/common/AttackTypeIcon'
import GlyphIcon from 'components/common/GlyphIcon'
import logoDatadog from 'assets/logos/datadog.svg'
import logoAWS from 'assets/logos/aws.svg'
import logoNewRelic from 'assets/logos/newrelic.svg'
import logoKubernetes from 'assets/logos/kubernetes.svg'
import logoAzure from 'assets/logos/azure.svg'
import logoPrometheus from 'assets/logos/prometheus.svg'
import logoSignalFx from 'assets/logos/signalfx.svg'
import logoVictorOps from 'assets/logos/victorops.svg'
import logoElasticSearch from 'assets/logos/elastic.svg'
import theme from 'theme'
import logoOpenShift from 'assets/logos/openshift.svg'
import logoMongoDb from 'assets/logos/mongodb.svg'
import logoMySQL from 'assets/logos/mysql.svg'
import logoCassandra from 'assets/logos/cassandra.svg'
import logoPostgreSQL from 'assets/logos/postgresql.svg'
import logoGCDatastore from 'assets/logos/google-cloud-datastore.svg'
import logoOracle from 'assets/logos/oracle.svg'
import logoGCPlatform from 'assets/logos/google-cloud-platform.svg'
import logoKafka from 'assets/logos/kafka.svg'
import logoActiveMQ from 'assets/logos/activemq.svg'
import logoRabbitMQ from 'assets/logos/rabbitmq.svg'
import logoGCStackdriver from 'assets/logos/google-cloud-stackdriver.svg'
import logoGrafana from 'assets/logos/grafana.svg'
import logoGCK8sEngine from 'assets/logos/google-cloud-platform-k8s-engine.svg'
import SvgCloudMigration from 'components/icons/CloudMigration'
import SvgPrivateCloud from 'components/icons/PrivateCloud'
import logoAmazonSQS from 'assets/logos/amazon-sqs.svg'
import logoAWSCloudwatch from 'assets/logos/aws-cloudwatch.svg'
import logoAmazonECS from 'assets/logos/amazon-ecs.svg'
import logoAmazonEKS from 'assets/logos/amazon-eks.svg'
import logoAmazonDynamoDB from 'assets/logos/aws-dynamodb.svg'
import SvgCloud from 'components/icons/CloudCompute'
import SvgCube from 'components/icons/ContainerOrchestration'
import useAuth from 'hooks/use-auth'
import Image from 'components/common/Image'
import { hasTruthy } from 'utils/list'

function CardFieldIcon({
  height,
  width,
  IconComponent,
  isSvg,
  defaultHeight,
  defaultWidth
}) {
  if (isSvg) {
    return (
      <IconComponent height={`${height || 40}px`} width={`${width || 40}px`} />
    )
  } else {
    return (
      <Image
        css={{
          height: height || defaultHeight,
          width: width || defaultWidth
        }}
        src={IconComponent}
      />
    )
  }
}

CardFieldIcon.propTypes = {
  height: PropTypes.number,
  width: PropTypes.number,
  IconComponent: PropTypes.node,
  isSvg: PropTypes.bool,
  defaultHeight: PropTypes.number,
  defaultWidth: PropTypes.number
}

CardFieldIcon.defaultProps = {
  isSvg: true
}

export const FIELD_CARDS_CONTENT = {
  'Amazon ECS': {
    name: 'Amazon ECS',
    badgeLabel: 'Getting started',
    title: 'Install Gremlin on Amazon Linux',
    buttonLabel: 'Read Installation Guide',
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoLinux}
      />
    ),

    href:
      'https://www.gremlin.com/community/tutorials/how-to-use-and-install-gremlin-on-amazon-linux/'
  },
  common: [
    {
      name: 'common',
      badgeLabel: 'Scenarios',
      title: 'Validate Auto-Scaling',
      buttonLabel: 'Add targets and run',
      buttonType: 'creative',
      labelColor: theme.colors.steel[2],
      icon: (height = theme.space[6], width = theme.space[6]) => (
        <GlyphIcon
          border={theme.border.base}
          css={{
            height,
            width
          }}
        >
          <AttackTypeIcon icon="cpu" />
        </GlyphIcon>
      ),
      to: '/scenarios/recommended/validate-auto-scaling/hosts'
    },
    {
      name: 'common',
      badgeLabel: 'community',
      title: 'Resources to make you the best chaos engineer you can be',
      buttonLabel: 'View all Tutorials',
      labelColor: theme.colors.blue[0],
      href: 'https://www.gremlin.com/community/tutorials/'
    },
    {
      name: 'common',
      badgeLabel: 'Scenarios',
      title: 'Use Gremlin to Reproduce the AWS S3 Outage',
      buttonLabel: 'Read the Guide',
      labelColor: theme.colors.steel[2],
      icon: (height = 40) => (
        <Image
          css={{
            height
          }}
          src={logoAWS}
        />
      ),
      href:
        'https://www.gremlin.com/community/tutorials/how-to-use-gremlin-scenarios-to-reproduce-the-aws-s-3-outage/#overview'
    }
  ],
  Datadog: [
    {
      name: 'Datadog',
      badgeLabel: 'Monitoring',
      title: 'Halt Gremlin Attacks Using the Datadog Webhook',
      buttonLabel: 'Read the Guide',
      labelColor: theme.colors.blue[0],
      icon: (height = 40) => (
        <Image
          css={{
            height
          }}
          src={logoDatadog}
        />
      ),
      href:
        'https://www.gremlin.com/community/tutorials/halting-gremlin-attacks-using-the-datadog-webhook/'
    },
    {
      name: 'Datadog',
      badgeLabel: 'Getting started',
      title: 'Create Chaos Dashboards with Datadog and Gremlin',
      buttonLabel: 'Read the Guide',
      labelColor: theme.colors.blue[0],
      icon: (height = 40) => (
        <Image
          css={{
            height
          }}
          src={logoDatadog}
        />
      ),
      href:
        'https://www.gremlin.com/community/tutorials/how-to-create-chaos-engineering-dashboards-with-datadog-and-gremlin/'
    }
  ],
  SignalFX: {
    name: 'SignalFX',
    badgeLabel: 'Monitoring',
    title: 'Chaos Engineering Monitoring & Metrics Guide',
    buttonLabel: 'Read the Guide',
    labelColor: theme.colors.blue[0],
    icon: (height = theme.space[4]) => (
      <Image
        css={{
          height
        }}
        src={logoSignalFx}
      />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/chaos-engineering-monitoring-metrics-guide/'
  },
  'Amazon EKS': {
    name: 'Amazon EKS',
    badgeLabel: 'Getting started',
    title: 'Install and use Gremlin with Amazon EKS',
    buttonLabel: 'Read Tutorial',
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoAmazonEKS}
      />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/how-to-install-and-use-gremlin-with-eks/'
  },

  'New Relic': {
    name: 'New Relic',
    badgeLabel: 'Getting started',
    title: 'Install and use Gremlin with New Relic',
    buttonLabel: 'Read installation Guide',
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoNewRelic}
      />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/chaos-engineering-with-gremlin-free-and-new-relic-infrastructure/'
  },
  OpsGenie: {
    name: 'OpsGenie',
    badgeLabel: 'Getting started',
    title: 'Chaos Engineering Monitoring & Metrics Guide',
    buttonLabel: 'Read installation Guide',
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoOpsGenie}
      />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/chaos-engineering-monitoring-metrics-guide/'
  },
  VictorOps: {
    name: 'VictorOps',
    badgeLabel: 'Monitoring',
    title: 'Chaos Engineering Monitoring & Metrics Guide',
    buttonLabel: 'Read the Guide',
    labelColor: theme.colors.blue[0],
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoVictorOps}
      />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/chaos-engineering-monitoring-metrics-guide/'
  },
  'Kubernetes - Other': {
    name: 'Kubernetes - Other',
    badgeLabel: 'Getting started',
    title: 'Install and use Gremlin with Kubernetes',
    buttonLabel: 'Read Installation Guide',
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoKubernetes}
      />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/how-to-install-and-use-gremlin-with-kubernetes/'
  },
  'Microsoft Azure': {
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoAzure}
      />
    )
  },
  DynamoDb: {
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoAmazonDynamoDB}
      />
    )
  },
  Azure: {
    name: 'Azure',
    badgeLabel: 'Attacks',
    title:
      "Utilize the Shutdown Attack with Gremlin's Shutdown Experiment Pack",
    buttonLabel: 'Read the Guide',
    labelColor: theme.colors.red[0],
    icon: (height = 40, width = 40) => (
      <logoAzure height={`${height}px`} width={`${width}px`} />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/shutdown-experiment-pack/'
  },
  Prometheus: {
    name: 'Prometheus',
    badgeLabel: 'Monitoring',
    title: 'Chaos Engineering for Prometheus',
    buttonLabel: 'Read the Guide',
    labelColor: theme.colors.blue[0],
    icon: (height = theme.space[6], width = theme.space[6]) => (
      <Image
        css={{
          height,
          width
        }}
        src={logoPrometheus}
      />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/chaos-engineering-for-prometheus/#introduction'
  },
  Elastic: {
    name: 'Elastic',
    badgeLabel: 'Monitoring',
    title: 'Chaos Engineering for ElasticSearch',
    buttonLabel: 'Read the Guide',
    labelColor: theme.colors.blue[0],
    icon: (height = theme.space[6]) => (
      <Image
        css={{
          height
        }}
        src={logoElasticSearch}
      />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/chaos-engineering-for-elasticsearch/#introduction'
  },
  AWS: {
    name: 'AWS',
    badgeLabel: 'Scenarios',
    title: 'Use Gremlin to Reproduce the AWS S3 Outage',
    buttonLabel: 'Read the Guide',
    labelColor: theme.colors.steel[2],
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoAWS}
      />
    ),
    href:
      'https://www.gremlin.com/community/tutorials/how-to-use-gremlin-scenarios-to-reproduce-the-aws-s-3-outage/#overview'
  },
  'Amazon Elastic Kubernetes Service': {
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoAmazonEKS}
      />
    )
  },
  'Amazon Elastic Container Service': {
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoAmazonECS}
      />
    )
  },
  OpenShift: {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoOpenShift}
      />
    )
  },
  'Custom Kubernetes': {
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoKubernetes}
      />
    )
  },
  MongoDb: {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoMongoDb}
      />
    )
  },
  MySQL: {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoMySQL}
      />
    )
  },
  Cassandra: {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoCassandra}
      />
    )
  },
  'GCP Datastore': {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoGCDatastore}
      />
    )
  },
  Postgres: {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoPostgreSQL}
      />
    )
  },
  'Amazon Web Services': {
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoAWS}
      />
    )
  },
  'Google Cloud Platform': {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoGCPlatform}
      />
    )
  },
  Oracle: {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoOracle}
      />
    )
  },
  'Private Cloud (On Premises)': {
    icon: (height = 40, width = 40) => (
      <SvgPrivateCloud height={`${height}px`} width={`${width}px`} />
    )
  },
  'Migrating Between Cloud Providers': {
    icon: (height = 40, width = 40) => (
      <SvgCloudMigration height={`${height}px`} width={`${width}px`} />
    )
  },
  Kafka: {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoKafka}
      />
    )
  },
  ActiveMQ: {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoActiveMQ}
      />
    )
  },
  RabbitMQ: {
    icon: (height = theme.space[3]) => (
      <Image
        css={{
          height
        }}
        src={logoRabbitMQ}
      />
    )
  },
  'AWS SQS': {
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoAmazonSQS}
      />
    )
  },
  'Amazon CloudWatch': {
    icon: (height = 40) => (
      <Image
        css={{
          height
        }}
        src={logoAWSCloudwatch}
      />
    )
  },
  'Google Operations (StackDriver)': {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoGCStackdriver}
      />
    )
  },
  Grafana: {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoGrafana}
      />
    )
  },
  'Google Kubernetes Engine': {
    icon: (height = theme.space[5]) => (
      <Image
        css={{
          height
        }}
        src={logoGCK8sEngine}
      />
    )
  }
}

export const TECH_STACK_DISPLAY_FIELDS = [
  'containers',
  'cloudProvider',
  'monitoring',
  'messaging',
  'databases'
]

export const TECH_STACK_DISPLAY_NAME_FIELDS = {
  databases: 'Databases',
  containers: 'Containers Orchestration',
  cloudProvider: 'Cloud Compute',
  monitoring: 'Monitoring',
  messaging: 'Messaging'
}

export const TECH_STACK_FIELDS_ICONS = {
  cloudProvider: SvgCloud,
  containers: SvgCube,
  databases: SvgDatabase,
  messaging: SvgMessaging,
  monitoring: SvgMonitoring
}

export default function useOnboardingData() {
  const { currentTeamId } = useAuth()
  const {
    data: prompts = { fields: [] },
    isLoading: promptsLoading
  } = useGremlinQuery(`/teams/${currentTeamId}/profile/prompt`)

  const { data: responses = {}, isLoading: responsesLoading } = useGremlinQuery(
    `/teams/${currentTeamId}/profile`
  )

  const stackFields = prompts.fields
    .slice(2)
    .filter(({ field }) => TECH_STACK_DISPLAY_FIELDS.includes(field))

  const teamSettingFields = prompts.fields.slice(0, 2)

  return {
    stackFields,
    teamSettingFields,
    fields: prompts.fields,
    responses,
    prompts,
    isLoading: hasTruthy(promptsLoading, responsesLoading)
  }
}
